import { Device, PageType } from '@/types/app';
import { InitializedPayment, PaymentConfig } from '@/types/payment';
import { getSite } from '@/utils/getSite';
import { getUrl } from '@/utils/getUrl';
import { http } from '@/utils/http';
import { isDev } from '@/utils/isDev';
import { logger } from '@/utils/logger';
import { AxiosError } from 'axios';
import https from 'https';
import { getOcelotConfig } from 'lib/configs/ocelot';

export const initPaymentRequest = async (alltIdUserCode: string) => {
  const response = await http
    .post<InitializedPayment>(
      '/api/payments/create-klarna-session',
      {
        alltIdUserCode,
      },
      { skipInterceptors: { response: { rejected: true } } },
    )
    .catch((error: AxiosError) => {
      if (error?.status === 409) {
        return { status: 409, data: null };
      }
    });

  const { data } = response || {};

  if (response?.status === 409) {
    logger.error(`initPaymentRequest: Cannot initialize payment for user: ${alltIdUserCode}`);
    throw new Error('Could not initialized payment request', {
      cause: { status: 409 },
    });
  }

  if (!data) {
    logger.error(`initPaymentRequest: Cannot initialize payment for user: ${alltIdUserCode}`);
    throw new Error('Could not initialized payment request');
  }

  return data;
};

export const createOrderRequest = async (
  alltIdUserCode: string,
  authorizationToken: string,
  dataAnalytics?: {
    pageId?: number;
    device?: Device;
    type?: PageType;
    publishedUrl?: string;
  },
) => {
  const response = await http.post('/api/payments/create-klarna-order', {
    alltIdUserCode,
    authorizationToken,
    dataAnalytics,
  });

  const { data } = response || {};

  if (!data) {
    logger.error(`createOrderRequest: Cannot create payment order for the user: ${alltIdUserCode}`);
    throw new Error('Could not create order');
  }

  return data;
};

export const getPaymentConfig = async () => {
  const paymentMiddlewareUrl = process.env.PAYMENT_MIDDLEWARE_URL;
  const xApiToken = process.env.PAYMENT_MIDDLEWARE_API_TOKEN;
  const purchaseTag = getOcelotConfig('features.payments.purchaseTag');
  const url = getUrl('/config/payment', paymentMiddlewareUrl);

  if (!purchaseTag) {
    return undefined;
  }

  if (!paymentMiddlewareUrl || !url || !xApiToken) {
    logger.error('Getting payment config: Internal server error');
    return undefined;
  }

  url.searchParams.set('brand', getSite());
  url.searchParams.set('purchaseTag', purchaseTag);

  const paymentConfig = await http.get(url.href, {
    httpsAgent: isDev ? new https.Agent({ rejectUnauthorized: false }) : undefined,
    headers: {
      letmein: 'foriamzeus',
      'X-Api-Token': xApiToken,
    },
  });

  if (!paymentConfig?.data) {
    return undefined;
  }

  return {
    purchaseTag,
    ...paymentConfig.data,
  } as PaymentConfig;
};
